import type { Theme, CSSObject } from '@mui/material/styles';
import type { PaletteColorKey } from '../palette';

import { varAlpha } from 'minimal-shared/utils';

import { dividerClasses } from '@mui/material/Divider';
import { checkboxClasses } from '@mui/material/Checkbox';
import { menuItemClasses } from '@mui/material/MenuItem';
import { autocompleteClasses } from '@mui/material/Autocomplete';

// ----------------------------------------------------------------------

/**
 * Generates styles for menu item components.
 *
 * @param theme - The MUI theme object.
 * @returns A CSS object with styles.
 *
 * @example
 * ...theme.mixins.menuItemStyles(theme)
 */

export function menuItemStyles(theme: Theme): CSSObject {
  return {
    ...theme.typography.body2,
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius * 0.75,
    '&:not(:last-of-type)': {
      marginBottom: 4,
    },
    [`&.${menuItemClasses.selected}`]: {
      fontWeight: theme.typography.fontWeightSemiBold,
      backgroundColor: theme.vars.palette.action.selected,
      '&:hover': { backgroundColor: theme.vars.palette.action.hover },
    },
    [`& .${checkboxClasses.root}`]: {
      padding: theme.spacing(0.5),
      marginLeft: theme.spacing(-0.5),
      marginRight: theme.spacing(0.5),
    },
    [`&.${autocompleteClasses.option}[aria-selected="true"]`]: {
      backgroundColor: theme.vars.palette.action.selected,
      '&:hover': { backgroundColor: theme.vars.palette.action.hover },
    },
    [`&+.${dividerClasses.root}`]: {
      margin: theme.spacing(0.5, 0),
    },
  };
}

// ----------------------------------------------------------------------

/**
 * Generates styles for paper components.
 *
 * @param theme - The MUI theme object.
 * @param options.blur - (Optional) Blur intensity in pixels. Defaults to 20.
 * @param options.color - (Optional) Background color. Defaults to semi-transparent paper color.
 * @param options.dropdown - (Optional) If true, applies padding, box-shadow, and border-radius for dropdowns.
 * @returns A CSS object with styles.
 *
 * @example
 * // Paper with default styles
 * ...theme.mixins.paperStyles(theme);
 *
 * @example
 * // Paper with dropdown styles and custom blur
 * ...theme.mixins.paperStyles(theme, {
 *   blur: 10,
 *   color: varAlpha(theme.vars.palette.background.defaultChannel, 0.9),
 *   dropdown: true
 * })
 */

export type PaperStyleOptions = {
  dropdown?: boolean;
};

export function paperStyles(theme: Theme, options?: PaperStyleOptions): CSSObject {
  const { dropdown } = options ?? {};
  return {
    ...(dropdown && {
      padding: theme.spacing(0.5),
      boxShadow: theme.vars.customShadows.dropdown,
      borderRadius: `${theme.shape.borderRadius * 1.25}px`,
    }),
  };
}

// ----------------------------------------------------------------------

/**
 * Generate style variant for components like Button or Chip
 *
 * @param theme - The MUI theme object.
 * @param colorKey - 'default', 'inherit', or a palette color key like 'primary', 'secondary', etc.
 * @param options.hover - (Optional) Enable hover styles or provide custom hover styles.
 * @returns A CSS object with styles.
 *
 * @example
 * // Filled styles
 * ...theme.mixins.filledStyles(theme, 'inherit', { hover: true })
 * ...theme.mixins.filledStyles(theme, 'inherit', { hover: { boxShadow: theme.vars.customShadows.z8 }, })
 *
 * // Soft styles
 * ...theme.mixins.softStyles(theme, 'inherit')
 * ...theme.mixins.softStyles(theme, 'primary', { hover: true })
 */

export type StyleOptions = {
  hover?: boolean | CSSObject;
};

export function filledStyles(
  theme: Theme,
  colorKey: 'default' | 'inherit' | PaletteColorKey,
  options?: StyleOptions,
): CSSObject {
  if (colorKey === 'default') {
    return {
      color: theme.vars.palette.grey[800],
      backgroundColor: theme.vars.palette.grey[300],
      ...(!!options?.hover && {
        '&:hover': {
          backgroundColor: theme.vars.palette.grey[400],
          ...(typeof options.hover === 'object' ? options.hover : {}),
        },
      }),
    };
  }

  if (colorKey === 'inherit') {
    return {
      color: theme.vars.palette.common.white,
      backgroundColor: theme.vars.palette.grey[800],
      ...theme.applyStyles('dark', {
        color: theme.vars.palette.grey[800],
        backgroundColor: theme.vars.palette.common.white,
      }),
      ...(!!options?.hover && {
        '&:hover': {
          backgroundColor: theme.vars.palette.grey[700],
          ...theme.applyStyles('dark', {
            backgroundColor: theme.vars.palette.grey[400],
          }),
          ...(typeof options.hover === 'object' ? options.hover : {}),
        },
      }),
    };
  }

  return {};
}

export function softStyles(
  theme: Theme,
  colorKey: 'default' | 'inherit' | PaletteColorKey,
  options?: StyleOptions,
): CSSObject {
  if (colorKey === 'default') {
    return filledStyles(theme, 'default', options);
  }

  if (colorKey === 'inherit') {
    return {
      backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
      ...(!!options?.hover && {
        '&:hover': {
          backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32),
          ...(typeof options.hover === 'object' ? options.hover : {}),
        },
      }),
    };
  }

  return {
    color: theme.vars.palette[colorKey].dark,
    backgroundColor: varAlpha(theme.vars.palette[colorKey].mainChannel, 0.16),
    ...theme.applyStyles('dark', {
      color: theme.vars.palette[colorKey].light,
    }),
    ...(!!options?.hover && {
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette[colorKey].mainChannel, 0.32),
        ...(typeof options.hover === 'object' ? options.hover : {}),
      },
    }),
  };
}
